import { useState, ReactNode } from "react";
import { Link } from "react-router-dom";
import { CheckIcon, ClipboardIcon } from "@heroicons/react/24/outline";

import { usePermissionValidator } from "../api/UserPermissionValidators";
import { useEdit360DueDate } from "../api/ThreeSixtyAPI";
import { ThreeSixtyBaseRow } from "../api/ThreeSixty";
import { GenericObject } from "./FilterSortHeader";
import { TableReportDateWithFormatAndClass } from "../shared-components/TableReportDateWithFormat";
import { Modal } from "../shared-components/Modal";
import { ChangeDueDateContainer } from "../reports/ChangeDueDateContainer";
import type { Workbench360LocationState } from "./workbenchEntrySource";
import {
  getRerunTooltipId,
  isRerunRow,
  ThreeSixtyRerunIcon,
  ThreeSixtyRerunTooltip,
} from "./ThreeSixtyRerunIcon";

export type ThreeSixtyRowRendererBaseProps<T extends ThreeSixtyBaseRow> = {
  row: T | GenericObject;
  cancel: string[];
  toggleCancel: (value: string) => number;
  /** Path to navigate when clicking the candidate name (e.g., "/workbench" or "/qa-workbench") */
  linkPath: string;
  /** Optional React Router location.state for the candidate link (e.g. web-search workbench variant) */
  linkState?: Workbench360LocationState;
  /** Determines if the candidate name should be a clickable link */
  canShowLink: boolean;
  /** Optional columns to render after the Check Type column (e.g., Misconduct Step badge) */
  afterCheckTypeColumns?: ReactNode;
  /** Optional columns to render after the Due Date column (e.g., Locked By) */
  afterDueDateColumns?: ReactNode;
};

export function ThreeSixtyRowRendererBase<T extends ThreeSixtyBaseRow>({
  row,
  cancel,
  toggleCancel,
  linkPath,
  linkState,
  canShowLink,
  afterCheckTypeColumns,
  afterDueDateColumns,
}: ThreeSixtyRowRendererBaseProps<T>) {
  const [copied, setCopied] = useState<boolean>(false);
  const [showDueDateModal, setShowDueDateModal] = useState<boolean>(false);
  const perms = usePermissionValidator();
  const isRerun = isRerunRow(row);
  const rerunTooltipId = getRerunTooltipId(row.request_uuid);

  const { mutateAsync: edit360DueDate } = useEdit360DueDate();

  async function handle360DueDateChange(newDate: Date): Promise<void> {
    row.due_date_updated = true;
    row.due_date = newDate.toISOString();
    try {
      await edit360DueDate({
        workflow_run_uuid: row.workflow_run_uuid,
        due_date: newDate.toISOString(),
      });
      setShowDueDateModal(false);
    } catch (except) {
      // do nothing
    }
  }

  function copyToClipboard(value: string) {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="flex gap-2 px-2 py-5 border-b text-gray-700">
      {/* Candidate Name Column */}
      <div className="flex-1 flex gap-2 overflow-auto pr-4">
        {perms.canCancel360 && (
          <div className="flex-0">
            <input
              type="checkbox"
              id={row.request_uuid}
              checked={cancel.includes(row.request_uuid)}
              onChange={() => toggleCancel(row.request_uuid)}
            />
          </div>
        )}
        <div className="flex-1">
          <div className="flex items-start">
            {canShowLink ? (
              <Link
                className="underline font-semibold"
                to={`/360/${row.request_uuid}${linkPath}`}
                state={linkState}
              >
                <span>{row.candidate_name}</span>
              </Link>
            ) : (
              <span className="font-semibold">{row.candidate_name}</span>
            )}
            {isRerun && <ThreeSixtyRerunIcon tooltipId={rerunTooltipId} />}
          </div>
          <div
            className="text-xs text-clip whitespace-nowrap mt-1 flex items-center text-gray-700 rounded-md py-1 hover:bg-gray-100 hover:cursor-pointer"
            onClick={() => copyToClipboard(row.request_uuid)}
          >
            {copied ? (
              <CheckIcon className="w-4 inline-block stroke-green-500" />
            ) : (
              <ClipboardIcon className="w-4 inline-block" />
            )}
            <span className="pl-1 text-gray-500">
              {row.request_uuid.slice(0, 8)}
            </span>
          </div>
        </div>
      </div>
      {isRerun && <ThreeSixtyRerunTooltip tooltipId={rerunTooltipId} />}

      {/* Company Column */}
      <div className="flex-1 flex items-center">{row.company_name}</div>

      {/* Check Type / Behavior Kit Column */}
      <div className="flex-1">
        <div className="text-md text-black">{row.check_type_name}</div>
        <div className="text-md mt-2 text-gray-500">
          {row.behavior_kit_name || "General"}
        </div>
      </div>

      {/* Optional columns after Check Type (e.g., Misconduct Step) */}
      {afterCheckTypeColumns}

      {/* Requested At Column */}
      <div className="flex-1 flex gap-2 whitespace-pre text-md items-center">
        <TableReportDateWithFormatAndClass date={row.requested_at} />
      </div>

      {/* Due Date Column */}
      <div className="flex-1 flex gap-2 whitespace-pre text-md items-center">
        {perms.canEdit360DueDate ? (
          <>
            <div
              className="flex gap-2 items-center underline"
              onClick={() => setShowDueDateModal(true)}
            >
              <TableReportDateWithFormatAndClass
                date={row.due_date}
                updated={row.due_date_updated}
                className="underline"
              />
            </div>
            <Modal
              open={showDueDateModal}
              onClose={() => setShowDueDateModal(false)}
              title="Change Due Date"
              width="w-full max-w-sm"
            >
              <ChangeDueDateContainer
                selectedDate={new Date(row.due_date)}
                createdAt={new Date()}
                handleDueDateChange={(newDate: Date) =>
                  handle360DueDateChange(newDate)
                }
              />
            </Modal>
          </>
        ) : (
          <TableReportDateWithFormatAndClass
            date={row.due_date}
            updated={row.due_date_updated}
          />
        )}
      </div>

      {/* Optional columns after Due Date (e.g., Locked By) */}
      {afterDueDateColumns}

      {/* Custom ID Column */}
      <div className="flex-1 flex items-center">{row.custom_id}</div>
    </div>
  );
}
